/**
 * ResourceCard
 * Displays a single resource (video or guide) as a clickable card.
 */

import {
  PlayCircleIcon,
  DocumentTextIcon,
  BookOpenIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { PlayCircleIcon as PlayCircleIconSolid } from '@heroicons/react/24/solid';

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const TYPE_CONFIG = {
  video: {
    label: 'Video',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    Icon: PlayCircleIcon,
    FallbackIcon: PlayCircleIconSolid,
    fallbackBg: 'bg-blue-50 dark:bg-blue-900/20',
    fallbackIcon: 'text-blue-300 dark:text-blue-700',
  },
  guide: {
    label: 'Guide',
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    Icon: DocumentTextIcon,
    FallbackIcon: BookOpenIcon,
    fallbackBg: 'bg-emerald-50 dark:bg-emerald-900/20',
    fallbackIcon: 'text-emerald-300 dark:text-emerald-700',
  },
};

/**
 * ResourceCard component
 * @param {Object} props
 * @param {Object} props.resource - Resource data object
 * @param {Function} props.onClick - Called when card is clicked
 */
const ResourceCard = ({ resource, onClick }) => {
  const config = TYPE_CONFIG[resource.type] ?? TYPE_CONFIG.guide;
  const { FallbackIcon } = config;

  return (
    <button
      type="button"
      onClick={() => onClick(resource)}
      className="group w-full text-left bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-primary dark:hover:border-primary hover:shadow-md transition-all overflow-hidden flex flex-col"
    >
      {/* Accent bar */}
      <div className="h-1 bg-gradient-to-r from-primary to-primary-dark group-hover:from-orange-400 group-hover:to-orange-500 transition-all" />

      {/* Thumbnail */}
      <div className={`relative w-full aspect-video flex items-center justify-center flex-shrink-0 ${config.fallbackBg}`}>
        {resource.thumbnailUrl ? (
          <img
            src={resource.thumbnailUrl}
            alt={resource.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <FallbackIcon className={`w-14 h-14 ${config.fallbackIcon}`} />
        )}
        {/* Play overlay for videos */}
        {resource.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors">
            <PlayCircleIconSolid className="w-12 h-12 text-white/0 group-hover:text-white/80 transition-all drop-shadow-lg" />
          </div>
        )}
        {/* "New" badge */}
        {resource.isNew && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wide shadow">
            New
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 flex flex-col flex-1 gap-2">
        {/* Type badge + category */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${config.badgeClass}`}>
            <config.Icon className="w-3 h-3" />
            {config.label}
          </span>
          {resource.categoryName && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
              {resource.categoryName}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2">
          {resource.title}
        </h3>

        {/* Description */}
        {resource.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 flex-1">
            {resource.description}
          </p>
        )}

        {/* Date */}
        {resource.createdAt && (
          <div className="flex items-center gap-1 mt-auto pt-1">
            <CalendarIcon className="w-3 h-3 text-gray-300 dark:text-gray-600" />
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {formatDate(resource.createdAt)}
            </span>
          </div>
        )}
      </div>
    </button>
  );
};

export default ResourceCard;
